import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  ShieldAlert, Plus, Download, Upload, History, Edit, Trash2, Search,
  Filter, Calendar, DollarSign, CheckCircle2, AlertTriangle, FileText, X, Loader2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { EmployeeSelect } from '../components/EmployeeAutocomplete';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const TDSOverrideRegister = () => {
  const { user } = useAuth();
  const canManage = ['tenant_admin', 'payroll_admin'].some(r => (user?.roles || [user?.role]).includes(r));

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [period, setPeriod] = useState(currentPeriod());
  const [employees, setEmployees] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyEmployee, setHistoryEmployee] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    employee_id: '',
    period: currentPeriod(),
    override_type: 'single_month',
    override_amount: '',
    total_months: 1,
    reason: '',
    remarks: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/companies`, { withCredentials: true })
      .then(({ data }) => {
        setCompanies(data);
        setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
      })
      .catch(() => {});
  }, [setSelectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      axios.get(`${API_URL}/employees?company_id=${selectedCompany}`, { withCredentials: true })
        .then(({ data }) => setEmployees(data))
        .catch(() => {});
    }
  }, [selectedCompany]);

  const fetchOverrides = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      let url = `${API_URL}/controls/tds-overrides?company_id=${selectedCompany}`;
      if (period) url += `&period=${period}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setOverrides(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch TDS overrides');
    }
    setLoading(false);
  }, [selectedCompany, period, statusFilter]);

  useEffect(() => { fetchOverrides(); }, [fetchOverrides]);

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.period || formData.override_amount === '') {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      if (editingOverride) {
        await axios.put(`${API_URL}/controls/tds-overrides/${editingOverride.id}`, {
          override_amount: Number(formData.override_amount),
          override_type: formData.override_type,
          total_months: Number(formData.total_months),
          reason: formData.reason,
          remarks: formData.remarks,
        }, { withCredentials: true });
        toast.success('TDS Override updated successfully');
      } else {
        await axios.post(`${API_URL}/controls/tds-overrides`, {
          company_id: selectedCompany,
          employee_id: formData.employee_id,
          period: formData.period,
          override_type: formData.override_type,
          override_amount: Number(formData.override_amount),
          total_months: Number(formData.total_months),
          reason: formData.reason,
          remarks: formData.remarks,
        }, { withCredentials: true });
        toast.success('TDS Override created successfully');
      }
      setShowCreateModal(false);
      setEditingOverride(null);
      fetchOverrides();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operation failed');
    }
    setSubmitting(false);
  };

  const handleDeactivate = async (overrideId) => {
    if (!window.confirm('Are you sure you want to deactivate this TDS override?')) return;
    try {
      await axios.delete(`${API_URL}/controls/tds-overrides/${overrideId}`, { withCredentials: true });
      toast.success('TDS Override deactivated');
      fetchOverrides();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Deactivation failed');
    }
  };

  const openHistory = async (empId, empName) => {
    setHistoryEmployee(empName);
    try {
      const { data } = await axios.get(`${API_URL}/controls/tds-overrides/history/${empId}`, { withCredentials: true });
      setHistoryLogs(data);
      setShowHistoryModal(true);
    } catch (e) {
      toast.error('Failed to load history');
    }
  };

  const downloadBulkTemplate = () => {
    window.open(`${API_URL}/controls/tds-overrides/template/download?company_id=${selectedCompany}&period=${period}`, '_blank');
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) { toast.error('Please select a CSV file'); return; }
    setBulkUploading(true);
    const formDataObj = new FormData();
    formDataObj.append('file', bulkFile);
    try {
      const { data } = await axios.post(
        `${API_URL}/controls/tds-overrides/template/upload?company_id=${selectedCompany}&period=${period}`,
        formDataObj,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );
      toast.success(data.message);
      setShowBulkModal(false);
      setBulkFile(null);
      fetchOverrides();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk upload failed');
    }
    setBulkUploading(false);
  };

  const exportRegister = (fmt) => {
    window.open(`${API_URL}/reports/tds-override-register?company_id=${selectedCompany}&period=${period}&fmt=${fmt}`, '_blank');
  };

  const filteredOverrides = overrides.filter(o => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      o.employee_code?.toLowerCase().includes(q) ||
      o.employee_name?.toLowerCase().includes(q) ||
      o.department?.toLowerCase().includes(q) ||
      o.reason?.toLowerCase().includes(q)
    );
  });

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="tds-override-page">
        {/* Header Banner */}
        <div className="flex items-start justify-between flex-wrap gap-4 bg-gradient-to-r from-slate-900 to-indigo-950 p-6 rounded-2xl text-white shadow-lg">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-rose-500/20 border border-rose-400/30 rounded-xl text-rose-300">
                <ShieldAlert className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">TDS Override & Carry Forward Register</h1>
                <p className="text-xs text-slate-300 mt-1 max-w-2xl">
                  Layer above the statutory TDS calculation engine. Supports individual monthly overrides, multi-month plans, automatic final-month liability reconciliation, and cumulative carry-forward balance management.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedCompany}
              onChange={e => setSelectedCompany(e.target.value)}
              className="h-10 px-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl text-sm text-white font-medium outline-none">
              {companies.map(c => <option key={c.id} value={c.id} className="text-slate-900">{c.name}</option>)}
            </select>
            <div className="flex items-center gap-1.5 h-10 px-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl text-sm text-white font-medium">
              <Calendar className="w-4 h-4 text-rose-300" />
              <input
                type="month"
                value={period}
                onChange={e => setPeriod(e.target.value)}
                className="outline-none text-sm bg-transparent text-white"
              />
            </div>
          </div>
        </div>

        {/* Toolbar & Filters */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-[280px]">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search employee code, name, department, or reason..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none">
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {canManage && (
              <>
                <Button
                  onClick={() => {
                    setEditingOverride(null);
                    setFormData({
                      employee_id: '',
                      period,
                      override_type: 'single_month',
                      override_amount: '',
                      total_months: 1,
                      reason: '',
                      remarks: '',
                    });
                    setShowCreateModal(true);
                  }}
                  className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl h-10">
                  <Plus className="w-4 h-4 mr-1.5" /> Individual Override
                </Button>
                <Button
                  onClick={() => setShowBulkModal(true)}
                  variant="outline"
                  className="rounded-xl h-10 border-slate-200 hover:bg-slate-50">
                  <Upload className="w-4 h-4 mr-1.5 text-indigo-600" /> Bulk Upload
                </Button>
              </>
            )}
            <Button
              onClick={() => exportRegister('excel')}
              variant="outline"
              className="rounded-xl h-10 border-slate-200 hover:bg-slate-50">
              <Download className="w-4 h-4 mr-1.5 text-emerald-600" /> Export Register
            </Button>
          </div>
        </div>

        {/* Override Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="py-24 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-rose-600 mb-2" /><p className="text-sm text-slate-500">Loading TDS Overrides...</p></div>
          ) : filteredOverrides.length === 0 ? (
            <div className="py-24 text-center text-slate-500">
              <ShieldAlert className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-base font-semibold text-slate-700">No TDS Overrides Found</p>
              <p className="text-xs text-slate-400 mt-1">No active or historical TDS overrides match the selected criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50 text-slate-500 text-[11px] font-semibold uppercase tracking-wider border-b border-slate-200">
                  <tr>
                    <th className="py-3.5 px-4">Employee</th>
                    <th className="py-3.5 px-3">Period</th>
                    <th className="py-3.5 px-3">Type</th>
                    <th className="py-3.5 px-3 text-right">Override TDS (₹)</th>
                    <th className="py-3.5 px-3 text-right">Carry Forward (₹)</th>
                    <th className="py-3.5 px-3">Reason</th>
                    <th className="py-3.5 px-3">Status</th>
                    <th className="py-3.5 px-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredOverrides.map((o) => (
                    <tr key={o.id || o._id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-900">{o.employee_name || '—'}</div>
                        <div className="text-xs text-slate-500 font-mono flex items-center gap-2">
                          <span>{o.employee_code}</span>
                          {o.department && <span>· {o.department}</span>}
                        </div>
                      </td>
                      <td className="py-3 px-3 font-medium text-slate-700">{o.period}</td>
                      <td className="py-3 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${o.override_type === 'multi_month' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {o.override_type === 'multi_month' ? `Multi-month (${o.remaining_months || 1}/${o.total_months || 1})` : 'Single Month'}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-slate-900">
                        ₹{Number(o.override_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-semibold text-rose-600">
                        ₹{Number(o.running_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-3 text-xs text-slate-600 max-w-[200px] truncate" title={o.reason}>{o.reason || '—'}</td>
                      <td className="py-3 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold uppercase ${
                          o.status === 'active' ? 'bg-emerald-100 text-emerald-800'
                          : o.status === 'completed' ? 'bg-slate-100 text-slate-700'
                          : 'bg-rose-100 text-rose-700'
                        }`}>
                          {o.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openHistory(o.employee_id, o.employee_name)}
                            title="View Audit History"
                            className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-600 hover:text-slate-900 transition-colors">
                            <History className="w-4 h-4" />
                          </button>
                          {canManage && o.status === 'active' && (
                            <>
                              <button
                                onClick={() => {
                                  setEditingOverride(o);
                                  setFormData({
                                    employee_id: o.employee_id,
                                    period: o.period,
                                    override_type: o.override_type || 'single_month',
                                    override_amount: o.override_amount,
                                    total_months: o.total_months || 1,
                                    reason: o.reason || '',
                                    remarks: o.remarks || '',
                                  });
                                  setShowCreateModal(true);
                                }}
                                title="Edit Override"
                                className="p-1.5 hover:bg-indigo-50 rounded-lg text-indigo-600 transition-colors">
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeactivate(o.id || o._id)}
                                title="Deactivate Override"
                                className="p-1.5 hover:bg-rose-50 rounded-lg text-rose-600 transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* CREATE / EDIT MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-rose-600" />
                {editingOverride ? 'Edit TDS Override' : 'New TDS Override Entry'}
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateOrUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Employee *</label>
                <EmployeeSelect
                  companyId={selectedCompany}
                  disabled={!!editingOverride}
                  value={formData.employee_id}
                  onChange={empId => setFormData({ ...formData, employee_id: empId })}
                  placeholder="Search Employee by Code, Name, Dept…"
                  data-testid="select-override-employee"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Period *</label>
                  <input
                    type="month"
                    value={formData.period}
                    disabled={!!editingOverride}
                    onChange={e => setFormData({ ...formData, period: e.target.value })}
                    required
                    className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-rose-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Override Type *</label>
                  <select
                    value={formData.override_type}
                    onChange={e => setFormData({ ...formData, override_type: e.target.value })}
                    className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-rose-500">
                    <option value="single_month">Single Month</option>
                    <option value="multi_month">Multi-Month Plan</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Override TDS Amount (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g. 5000"
                    value={formData.override_amount}
                    onChange={e => setFormData({ ...formData, override_amount: e.target.value })}
                    required
                    className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono font-semibold text-slate-900 outline-none focus:bg-white focus:border-rose-500"
                  />
                </div>
                {formData.override_type === 'multi_month' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Total Months *</label>
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={formData.total_months}
                      onChange={e => setFormData({ ...formData, total_months: e.target.value })}
                      required
                      className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-rose-500"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Reason *</label>
                <input
                  type="text"
                  placeholder="e.g. Employee requested additional tax deduction for IT declaration"
                  value={formData.reason}
                  onChange={e => setFormData({ ...formData, reason: e.target.value })}
                  required
                  className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-rose-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Remarks</label>
                <textarea
                  rows={2}
                  placeholder="Additional notes..."
                  value={formData.remarks}
                  onChange={e => setFormData({ ...formData, remarks: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-rose-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="rounded-xl">Cancel</Button>
                <Button type="submit" disabled={submitting} className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl">
                  {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                  {editingOverride ? 'Save Changes' : 'Create Override'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BULK UPLOAD MODAL */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                <Upload className="w-5 h-5 text-indigo-600" /> Bulk TDS Override Upload
              </h3>
              <button onClick={() => setShowBulkModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3.5 text-xs text-indigo-900 space-y-2">
                <p className="font-semibold">Bulk Upload Instructions:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Download pre-populated template with active employees.</li>
                  <li>Enter <code>Override Amount</code> and <code>Override Type</code>.</li>
                  <li>Upload completed CSV file.</li>
                </ul>
                <Button onClick={downloadBulkTemplate} size="sm" variant="outline" className="mt-1 border-indigo-300 text-indigo-800 bg-white hover:bg-indigo-100">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download CSV Template
                </Button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Select CSV File</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={e => setBulkFile(e.target.files?.[0])}
                  className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowBulkModal(false)} className="rounded-xl">Cancel</Button>
                <Button onClick={handleBulkUpload} disabled={bulkUploading || !bulkFile} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl">
                  {bulkUploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Upload Overrides
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AUDIT HISTORY MODAL */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-600" /> Audit History — {historyEmployee}
              </h3>
              <button onClick={() => setShowHistoryModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-3 flex-1">
              {historyLogs.length === 0 ? (
                <p className="text-center text-slate-400 py-8 text-sm">No audit history recorded.</p>
              ) : (
                <div className="relative border-l-2 border-indigo-100 ml-3 space-y-4">
                  {historyLogs.map((log, idx) => (
                    <div key={idx} className="relative pl-6">
                      <span className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-indigo-600 ring-4 ring-white" />
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-xs space-y-1">
                        <div className="flex items-center justify-between font-semibold text-slate-900">
                          <span className="capitalize text-indigo-700 font-bold">{log.action?.replace(/_/g, ' ')}</span>
                          <span className="text-slate-400 font-mono text-[10px]">{log.entered_date ? new Date(log.entered_date).toLocaleString('en-IN') : '—'}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1 font-mono text-slate-700">
                          <div>System TDS: <span className="font-bold">₹{log.system_tds ?? '—'}</span></div>
                          <div>Override TDS: <span className="font-bold text-rose-600">₹{log.override_tds ?? log.effective_tds ?? '—'}</span></div>
                          <div>Diff: <span className="font-bold">₹{log.difference ?? '—'}</span></div>
                          <div>Balance: <span className="font-bold text-indigo-600">₹{log.running_balance ?? '—'}</span></div>
                        </div>
                        {log.reason && <p className="text-slate-600 pt-1">Reason: <i>{log.reason}</i></p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default TDSOverrideRegister;
